/**
 * What: Lets a merchant review a saved Shopify study setup and start its final StudyRun.
 * Transport / data-flow: Runs a server-authoritative dry run through the Shopify proxy, displays its quoted credit spend inline, then submits the accepted preflight with one explicit action.
 * Algorithm: Dry-run on entry -> show readiness, timing, and credit spend inline -> submit with the accepted preflight hash.
 */
import { type StudyAuthoringRecord, type StudyAuthoringState, type AutomaticResponsePhraseBudget } from '@testfeed/shopify-contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shopifyComponentCopy } from '@testfeed/product-language';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAnimateWhenVisible } from '../hooks/useAnimateWhenVisible';
import { useShopifyStudyActivity } from '../ShopifyStudyActivityContext';
import { buildShopifyAppScopeKey, useShopifyAppContext } from '../ShopifyAppContext';
import { useShopifyScopeLifecycle } from '../hooks/useShopifyScopeLifecycle';
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies';
import { studyRunPreflightQueryKey } from '../queries/studyQueries';
import { testFeedUsageActivityQueryKey } from '../hooks/useTestFeedUsageActivity';
import { testFeedUsageQueryKey } from '../hooks/useTestFeedUsage';
import type { shopifyApiFetch } from '../shopifyApiFetch';
import { shopifyApiClient } from '../shopifyApiClient';
import { reportClientDiagnostic } from '../clientDiagnostics';
import { describeErrorForDiagnostics } from '../merchantErrors';
import { CreditExhaustedPrompt } from './CreditExhaustedPrompt';
import { ShopifyPageSkeleton, ShopifySkeletonText } from './ShopifyPageSkeleton';
import { buildDirectStudyResultsViewUrl } from '../resultsNavigation';
import './results/ApplyCardView.css';
import { createRunRequestId, followedRun, getRunStatus, getRunSummaryBody, formatCreditBalance, formatCreditUse, readDryRunOutcome, readRunPayload, readSubmissionOutcome, type DryRunState, type StudyRunRequestResult, type SubmissionRequestResult } from './studyRunDisplay';
import { FooterHelp, HelpModal, InfoButton, InfoPopover } from './shared/PageHelp';
import { shopifyUiTracker, shopifyUiFailure, trackShopifyAction } from '../shopifyUiTracking';
import { isAbortError } from '../page-state';

const HELP_MODAL = 'study-run-help-modal';
const CREDIT_INFO = 'study-run-credit-info';

/** Reviews a persisted setup, performs the mandatory dry run, and starts the paid run. */
export function StudyRunPage({
  shopify,
  studyId,
  authoring,
  authoringState,
  onRunStarted,
  isLoadingSetup,
  onBackToStudies,
}: {
  shopify: Parameters<typeof shopifyApiFetch>[0];
  studyId: string;
  authoring?: StudyAuthoringRecord;
  authoringState?: StudyAuthoringState;
  /** Re-reads the server status so the page follows the started run instead of a browser-only flag. */
  onRunStarted: () => void;
  isLoadingSetup: boolean;
  onBackToStudies: () => void;
}) {
  const copy = shopifyComponentCopy.studyRunPage;
  const { trackPendingRun } = useShopifyStudyActivity();
  const { shopDomain, orgId, connectionId } = useShopifyAppContext();
  const queryClient = useQueryClient();
  // The study+scope a run was started for; compared against current props to derive
  // hasStarted, so prop changes reset the running state without effect-synced mirrors.
  const [startedScope, setStartedScope] = useState<{ studyId: string; scopeToken: string } | null>(null);
  const { ref: runButtonRef, hasBeenVisible: runButtonVisible } = useAnimateWhenVisible<HTMLSpanElement>();
  const { ref: viewProgressButtonRef, hasBeenVisible: viewProgressButtonVisible } = useAnimateWhenVisible<HTMLSpanElement>();
  const studyIdRef = useRef(studyId);
  const scopeKey = buildShopifyAppScopeKey({ shopDomain, orgId, connectionId });
  const { token: scopeToken, isActive: isActiveScope } = useShopifyScopeLifecycle(scopeKey);
  useEffect(() => {
    studyIdRef.current = studyId;
  }, [studyId]);

  const dryRunQuery = useQuery({
    queryKey: studyRunPreflightQueryKey(shopDomain, orgId, connectionId, studyId),
    enabled: Boolean(studyId) && !isLoadingSetup && Boolean(orgId && connectionId),
    retry: false,
    ...shopifyQueryPolicies.runPreflight,
    queryFn: async (): Promise<StudyRunRequestResult> => {
      const requestStudyId = studyId;
      if (!requestStudyId) throw new Error('Dry run requires a study ID');
      try {
        const response = await shopifyApiClient(shopify).api['study-authoring-run'].$post({
          json: { studyId: requestStudyId, dryRun: true, targetScope: 'study' },
        });
        return { response, payload: await readRunPayload(response) };
      } catch (error) {
        console.error(describeErrorForDiagnostics(error));
        reportClientDiagnostic(shopify, { schema: 'shopify-study-dry-run', paths: ['dry_run_failed'] });
        throw error;
      }
    },
  });

  const runIntent = useRef<{ key: string; id: string } | null>(null);
  const runMutation = useMutation({
    mutationFn: async ({ studyId: submitStudyId, hash, scopeToken: requestScopeToken }: { studyId: string; hash: string; scopeToken: string }): Promise<SubmissionRequestResult> => {
      const intentKey = `${requestScopeToken}:${submitStudyId}:${hash}`;
      if (runIntent.current?.key !== intentKey) runIntent.current = { key: intentKey, id: createRunRequestId() };
      const runRequestId = runIntent.current.id;
      const tracker = shopifyUiTracker(shopify);
      const attempt = tracker.request('run-study');
      return shopifyApiClient(shopify).api['study-authoring-run'].$post({
        json: { studyId: submitStudyId, dryRun: false, acknowledgedPreflightHash: hash, runRequestId, targetScope: 'study' },
      }).then(async (response) => {
        const payload = await readRunPayload(response);
        const result = { studyId: submitStudyId, response, payload, scope: { shopDomain, orgId, connectionId }, scopeToken: requestScopeToken };
        // A resolved HTTP refusal is not an accepted study run. Keep the existing result UI unchanged.
        if (readSubmissionOutcome(result).started) tracker.completed(attempt);
        else tracker.failed(attempt, shopifyUiFailure({ status: response.status }));
        return result;
      }).catch((cause: unknown) => {
        if (isAbortError(cause)) tracker.cancelled(attempt, 'aborted');
        else tracker.failed(attempt, shopifyUiFailure(cause));
        throw cause;
      });
    },
    onSuccess: (result, variables) => {
      if (!readSubmissionOutcome(result).started || !result.payload.studyRunId) return;
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: testFeedUsageQueryKey(result.scope.shopDomain, result.scope.orgId, result.scope.connectionId) }),
        queryClient.invalidateQueries({ queryKey: testFeedUsageActivityQueryKey(result.scope.shopDomain, result.scope.orgId, result.scope.connectionId) }),
      ]);
      if (studyIdRef.current !== variables.studyId || !isActiveScope(result.scopeToken)) return;
      trackPendingRun(result.payload.studyRunId, studyId);
      setStartedScope({ studyId: variables.studyId, scopeToken: variables.scopeToken });
      onRunStarted();
    },
    onError: (error, variables) => {
      if (studyIdRef.current !== variables.studyId) return;
      console.error(describeErrorForDiagnostics(error));
    },
  });

  const dryRun = readDryRunOutcome(dryRunQuery.data);
  const dryRunState: DryRunState = dryRunQuery.isPending ? 'checking' : dryRunQuery.isError ? 'error' : dryRun.state;
  const dryRunPageError = dryRunQuery.isError
    ? shopifyComponentCopy.shopifyApiErrors.couldNotStartRun006
    : dryRun.pageError;
  const currentSubmission = runMutation.data && runMutation.data.studyId === studyId && runMutation.data.scopeToken === scopeToken ? runMutation.data : undefined;
  const submission = readSubmissionOutcome(currentSubmission);
  const submittedRunId = currentSubmission?.payload.studyRunId ?? null;
  const isSubmitting = runMutation.isPending && runMutation.variables?.studyId === studyId && runMutation.variables.scopeToken === scopeToken;
  const preflight = dryRun.preflight;
  const hasStartedCurrentStudy = startedScope?.studyId === studyId && startedScope?.scopeToken === scopeToken;
  const creditPrompt = dryRun.prompt ?? submission.prompt;
  const spend = submission.prompt ? submission.spend : dryRun.spend;
  const availableAfterNextRun = submission.prompt
    ? submission.availableAfterNextRun
    : dryRun.availableAfterNextRun;
  const pageError = runMutation.isError ? shopifyComponentCopy.shopifyApiErrors.couldNotStartRun006 : submission.pageError ?? dryRunPageError;

  const handleRunStudy = () => {
    if (runMutation.isPending) return;
    if (!studyId) return;
    const hash = preflight?.preflightHash;
    if (typeof hash !== 'string' || !hash) { shopifyUiTracker(shopify).blocked('run-study', 'validation'); return; }
    runMutation.mutate({ studyId, hash, scopeToken });
  };

  const retryReadiness = async () => {
    if (dryRunQuery.isFetching) return;
    const tracker = shopifyUiTracker(shopify);
    const attempt = tracker.request('retry-load');
    try {
      const result = await dryRunQuery.refetch();
      if (result.isError) tracker.failed(attempt, shopifyUiFailure(result.error));
      else if (!result.data?.response.ok || !result.data.payload.success) tracker.failed(attempt, { errorCode: 'REQUEST_FAILED', ...(result.data ? { httpStatus: result.data.response.status } : {}) });
      else tracker.completed(attempt);
    } catch (cause) {
      console.error('STUDY_RUN_READINESS_RETRY_FAILED');
      tracker.failed(attempt, shopifyUiFailure(cause));
    }
  };

  if (isLoadingSetup) return <ShopifyPageSkeleton accessibilityLabel={copy.checkingReadiness} />;
  if (!authoring) return <s-banner tone="warning">{copy.missingSetupBody}</s-banner>;

  const audienceLabel = `Ages ${authoring.draft.audienceDraft.minimumAge}–${authoring.draft.audienceDraft.maximumAge}, ${authoring.draft.audienceDraft.marketCountry}`;
  const startedRun = { hasStarted: hasStartedCurrentStudy, submittedRunId, authoringState };
  // The server's run this page shows: the one it started, or after a reload the saved test's latest run.
  const shownRun = followedRun(startedRun);
  const progressRunId = hasStartedCurrentStudy ? submittedRunId : shownRun?.latestRunId ?? null;
  const isReady = dryRunState === 'ready' && Boolean(preflight) && !hasStartedCurrentStudy && shownRun?.status !== 'running';
  const status = getRunStatus({ dryRunState, isReady, ...startedRun });
  const summaryBody = getRunSummaryBody(startedRun);
  const showRetry = Boolean(creditPrompt) || ((dryRunState === 'error' || dryRunState === 'blocked') && !dryRun.noPlan);
  const isMissingPlan = dryRun.noPlan;
  const capacityBalanceText = spend && availableAfterNextRun !== null
    ? formatCreditBalance(spend, availableAfterNextRun)
    : null;
  const capacityUseText = spend ? formatCreditUse(spend) : null;
  let runAction: ReactNode = null;
  if (showRetry) {
    runAction = <s-button onClick={() => { void retryReadiness(); }} loading={dryRunState === 'checking'}>{copy.retryReadinessAction}</s-button>;
  } else if (!isMissingPlan) {
    runAction = <span ref={runButtonRef} className={isReady && runButtonVisible ? 'tf-attention-bounce' : undefined}><s-button variant="primary" onClick={handleRunStudy} loading={isSubmitting} disabled={!isReady}>{copy.runStudyAction}</s-button></span>;
  }

  return (
    <s-section>
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="block" gap="small-200">
            {/* h2: the shell's Polaris Page title is this page's only h1. */}
            <s-heading>{copy.pageTitle}</s-heading>
            <s-text type="strong">{authoring.draft.briefDraft.studyName}</s-text>
          </s-stack>
          <s-badge tone={status.tone}>{status.label}</s-badge>
        </s-stack>
        <s-divider />
        <s-stack direction="inline" gap="large-500">
          <s-stack direction="block" gap="small-200">
            <s-text type="strong">{copy.productLabel}</s-text>
            <s-paragraph>{authoring.draft.selectedProduct?.title}</s-paragraph>
          </s-stack>
          <s-stack direction="block" gap="small-200">
            <s-text type="strong">{copy.audienceLabel}</s-text>
            <s-paragraph>{audienceLabel}</s-paragraph>
          </s-stack>
        </s-stack>
        {summaryBody && <s-paragraph color="subdued">{summaryBody}</s-paragraph>}
        {progressRunId && (
          <span ref={viewProgressButtonRef} className={viewProgressButtonVisible ? 'tf-attention-bounce' : undefined}><s-button href={buildDirectStudyResultsViewUrl(studyId, progressRunId)} variant="primary" onClick={() => trackShopifyAction(shopify, 'open-results', () => undefined)}>{copy.viewTestProgressAction}</s-button></span>
        )}
        {isReady && (
          <s-stack direction="block" gap="small-200">
            <s-text type="strong">{copy.runDetailsHeading}</s-text>
            <s-paragraph color="subdued">{copy.leavePageBody}</s-paragraph>
            {capacityUseText && (
              <s-stack direction="inline" gap="small-200" alignItems="center">
                <s-paragraph>{capacityUseText}</s-paragraph>
                <InfoButton id={CREDIT_INFO} heading={copy.creditInfoHeading} />
              </s-stack>
            )}
            {capacityBalanceText && <s-paragraph color="subdued">{capacityBalanceText}</s-paragraph>}
          </s-stack>
        )}
        <AutomaticEnrichmentBudget budget={dryRunQuery.data?.payload.preflight?.automaticEnrichment} />
        {capacityUseText && (
          <InfoPopover id={CREDIT_INFO} why={copy.creditInfoWhy} how={copy.creditInfoHow} />
        )}
        {creditPrompt && <CreditExhaustedPrompt spend={creditPrompt} />}
        {isMissingPlan && <s-banner tone="warning">{copy.missingPlanBody}</s-banner>}
        {pageError && <s-banner tone="critical">{pageError}</s-banner>}
        {!hasStartedCurrentStudy && !creditPrompt && dryRunState === 'checking' && (
          <s-stack direction="block" gap="small-200">
            <s-paragraph color="subdued">{copy.checkingReadiness}</s-paragraph>
            <ShopifySkeletonText lines={2} />
          </s-stack>
        )}
        {!hasStartedCurrentStudy && (
          <s-stack direction="inline" gap="small-300">
            <s-button onClick={() => trackShopifyAction(shopify, 'navigate-studies', onBackToStudies)}>{copy.backToStudiesAction}</s-button>
            {runAction}
          </s-stack>
        )}
      </s-stack>
      <FooterHelp modalId={HELP_MODAL} link="how running a test works" />
      <HelpModal id={HELP_MODAL} heading={copy.helpModalHeading}>
        <s-paragraph>{copy.helpModalBody1}</s-paragraph>
        <s-paragraph color="subdued">{copy.helpModalBody2}</s-paragraph>
      </HelpModal>
    </s-section>
  );
}

/** Budget wording and quantities are supplied by the accepted server preview. */
function AutomaticEnrichmentBudget({ budget }: { budget?: AutomaticResponsePhraseBudget }) {
  if (!budget?.enabled) return null;
  return (
    <s-stack direction="block" gap="small-200">
      <s-text type="strong">{budget.display.heading}</s-text>
      {budget.display.lines.map((line) => <s-paragraph key={line} color="subdued">{line}</s-paragraph>)}
    </s-stack>
  );
}
